/*
 * Copyright (c) 2020. Legalbird eine Marke der R&S Internet Jewels GmbH
 */

import React from "react";
import guidebookContentStyle from "../guidebookContentStyle";
import TextContainer from "../../Container/TextContainer";
import Layout from "../../../containers/Layouts/Layout";
import TeaserSlogan from "../../Teaser/TeaserSlogan";
import HighlightBox from "../../Box/HighlightBox/HighlightBox";
import QuestionsContactSection from "../QuestionsContactSection/QuestionsContactSection";
import DoubleButton from "../../Button/DoubleButton";
import { Helmet } from "react-helmet-async";
import Button from "@mui/material/Button";
import Grid from "@mui/material/Grid";
import backgroundTeaser from "../../../assets/img/guidebook/Teaser_Themen-im-Überblick_700px.jpg";
import Typography from "@mui/material/Typography";
import { Link as MuiLink, Paper } from "@mui/material";
import { gaSet } from "../../../services/googleAnalyticsService";
import { Link } from "react-router";

const GuidebookDivorceImportantTopics = () => {
  const title = "Ratgeber - Wichtige Themen im Überblick | Legalbird";

  gaSet({ title: title });

  return (
    <Layout backgroundType={"serviceworld"}>
      <Helmet>
        <title>{title}</title>
        <meta
          name="description"
          content="Was muss ich bei einer Scheidung bedenken und welche Themen sollte ich mit meinem Partner abklären? Wir
            geben Ihnen einen Überblick über die wichtigsten Themen."
        />
      </Helmet>
      <Paper sx={guidebookContentStyle.paper}>
        <TeaserSlogan
          slogan={"Alle wichtigen Themen im Überblick - Was kommt auf mich zu?"}
          backgroundImagePath={backgroundTeaser}
          height={300}
          headingColor={"#333333"}
        />
        <TextContainer>
          <p>
            Was muss ich bei einer Scheidung bedenken und welche Themen sollte ich mit meinem Partner abklären? Wir geben Ihnen einen Überblick über die
            wichtigsten Themen.
          </p>
          <hr />
          <Typography variant={"h4"} sx={guidebookContentStyle.subheader}>
            Einigkeit mit Ihrem Partner erleichtert die Scheidung enorm
          </Typography>
          <p>
            Wenn Sie sich scheiden lassen möchten, sollten Sie offen und in Ruhe verschiedene Themen mit Ihrem Partner abklären und diese bestenfalls
            schriftlich festhalten. Dies erleichtert den Scheidungsprozess und verbessert auch die Stimmung zwischen Ihnen und Ihrem Partner. Denn neben dem
            gerichtlichen Prozess löst eine Einreichung der Scheidung eine Reihe von Folgen aus, die Sie abklären sollten, damit Sie sich mit Ihrem Partner
            einig sind und somit die Trennung nicht unnötig kompliziert wird. Themen, die eine Scheidung zusätzlich zum eigentlichen Scheidungsprozess mit sich
            bringt, werden unter dem Begriff Scheidungsfolgesachen zusammen gefasst.
          </p>
          <HighlightBox headline={"Was sind die Scheidungsfolgesachen?"}>
            Über folgendeThemen sollten Sie sich mit Ihrem Partner austauschen und einigen:
            <ul>
              <li>Ehegattenunterhalt</li>
              <li>Verteilung von Besitz/Hausrat und ehelicher Wohnung</li>
            </ul>
            Wenn Sie gemeinsame, minderjährige Kinder haben, müssen zusätzlich geklärt werden:
            <ul>
              <li>Sorgerecht</li>
              <li>Umgangsrecht</li>
              <li>Kindesunterhalt</li>
            </ul>
            <span>
              Tipp: Sie finden im{" "}
              <MuiLink component={Link} sx={guidebookContentStyle.link} to={"/ratgeber/familienrecht"}>
                Ratgeber
              </MuiLink>{" "}
              zu all diesen Themen detaillierte Spezial-Artikel
            </span>
          </HighlightBox>
          <p>
            Für Scheidungsfolgesachen ist keine Klärung vor Gericht nötig, wenn Sie sich mit Ihrem Partner einig werden. Wenn Sie sich bei bestimmten
            Folgesachen nicht einigen können, können Sie zur Klärung einen Antrag beim Familiengericht einreichen, beispielsweise zu der Frage, ob Ihr Partner
            Ihnen Unterhalt schuldet. Ein solches gerichtliches Verfahren hat zunächst nichts mit Ihrem Scheidungsverfahren zu tun, sondern läuft quasi parallel
            hierzu. Allerdings besteht auch die Möglichkeit, diese Verfahren in den sogenannten Scheidungsverbund mit hineinzunehmen. Dies hat zur Folge, dass
            das Gericht über Ihre Scheidung nur zusammen mit dem Unterhaltsverfahren entscheiden kann. Hierdurch wird das Scheidungsverfahren fast immer sehr
            langwierig und insgesamt sehr teuer. Daher ist es sehr empfehlenswert, alles direkt mit dem Partner abzuklären. Wenn Sie sich über die Themen
            geeinigt haben, sollten Sie am Besten alles in einer Scheidungsfolgenvereinbarung schriftlich festhalten. Für die Regelung bestimmter Folgesachen,
            z.B. Güterstandsvereinbarungen, oder dem Ausschluss des Versorgungsausgleichs ist eine notarielle Beurkundung gesetzlich vorgeschrieben.
          </p>
          <Typography variant={"h4"} sx={guidebookContentStyle.subheader}>
            Alles Wichtige zur Scheidungsfolgenvereinbarung
          </Typography>
          <p>
            Durch eine Scheidungsfolgenvereinbarung legen die Ehepartner Regeln fest, die nach dem Scheidungsverfahren und nach der rechtskräftigen Scheidung
            gelten sollen. Dies ermöglicht eine reibungslose und offene Klärung der verschiedenen Themen und verhindert, dass es zu einer sehr teuren,
            streitigen Scheidung mit Folgesachen vor Gericht kommt. Die Vereinbarung muss in manchen Fällen notariell beurkundet werden und sollte ggf. zusammen
            mit einem Anwalt erstellt werden, um unzulässige Klauseln zu vermeiden und ein rechtskräftiges Dokument zu erstellen.
          </p>

          <Typography variant={"h4"} sx={guidebookContentStyle.subheader}>
            Kostenlose Vorlage
          </Typography>
          <p>
            Sie können sich hier ein Muster für eine Scheidungsfolgenvereinbarung herunterladen. So erhalten Sie einen ersten Eindruck über das Format und
            mögliche Inhalte. Sie müssen dieses Muster natürlich noch auf Ihre individuellen Ansprüche anpassen.
          </p>
          <Grid container>
            <Grid
              size={{
                xs: 12,
                sm: 6
              }}>
              <Button
                component={"a"}
                href={"/documents/Legalbird_Vorlage_Scheidungsfolgenvereinbarung.pdf"}
                download={"Scheidungsfolgenvereinbarung"}
                sx={guidebookContentStyle.downloadButton}
                fullWidth
                variant={"text"}
              >
                Download pdf
              </Button>
            </Grid>
            <Grid
              size={{
                xs: 12,
                sm: 6
              }}>
              <Button
                component={"a"}
                href={"/documents/Legalbird_Vorlage_Scheidungsfolgenvereinbarung.docx"}
                download={"Scheidungsfolgenvereinbarung"}
                sx={guidebookContentStyle.downloadButton}
                fullWidth
                variant={"text"}
              >
                Download Word Datei
              </Button>
            </Grid>
          </Grid>
          <Typography variant={"h4"} sx={guidebookContentStyle.subheader}>
            Steuern und Versicherung
          </Typography>
          <p>
            In dem Kalenderjahr, in dem Sie sich getrennt haben, können Sie ihre Steuerklasse noch behalten, spätestens in dem Folgejahr muss die Steuerklasse
            dann in die Klasse I oder II geändert werden. Tipp: Die Kosten einer Scheidung sind in vollem Umfang steuerlich absetzbar! Alle Aufwendungen aus dem
            Scheidungsverfahren können als außergewöhnliche Belastung steuerwirksam geltend gemacht werden.
          </p>
          <p>
            Auch Ihre Krankenversicherung ändert sich nach der Scheidung. Sie sind so lange in der Familienversicherung mit versichert bis Sie rechtskräftig
            geschieden worden sind. Sie sind also auch während der Trennunszeit noch familienversichert. Erst für die Zeit nach der Scheidung müssen Sie mit
            Ihrer Krankenversicherung Kontakt aufnehmen und sich um eine eigene Krankenversicherung kümmern.
          </p>
          <Typography variant={"h4"} sx={guidebookContentStyle.subheader}>
            Ihre Scheidung
          </Typography>
          <p>
            Abgesehen von den Scheidungsfolgen, die Sie möglichst vorab mit Ihrem Partner klären sollten, muss natürlich der Scheidungsantrag bei Gericht
            eingereicht werden. Sie können ganz einfach alle Angaben für den Antrag direkt hier eingeben, wir erstellen den Scheidungsantrag und reichen ihn bei
            Gericht für Sie ein.
          </p>
          <DoubleButton buttonText={"zum Scheidungsantrag"} buttonLink={"/servicewelt"} backText={"Zurück zum Ratgeber"} backLink={"/ratgeber/familienrecht"} />
          <QuestionsContactSection />
        </TextContainer>
      </Paper>
    </Layout>
  );
};

export default GuidebookDivorceImportantTopics;
